import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  clearMySubscription,
  getMySubscription,
  saveMySubscription,
} from "@/lib/utils";
import RazorpayService from "@/services/api/razorpay";
import SubscriptionService from "@/services/api/subscription";
import type {
  SubscribedUser,
  GetSubscriptionResponse,
  GetSubscriptionParams,
  SubscriptionPlan,
} from "@/types/subscription";

interface SubscriptionState {
  currentSubscription: SubscribedUser | null;
  subscriptionHistory: SubscribedUser[];
  plans: SubscriptionPlan[];
  isLoadingCurrent: boolean;
  isLoadingHistory: boolean;
  isLoadingPlans: boolean;
  error: string | null;
  historyError: string | null;
  plansError: string | null;
}

const initialState: SubscriptionState = {
  currentSubscription: getMySubscription(),
  subscriptionHistory: [],
  plans: [],
  isLoadingCurrent: false,
  isLoadingHistory: false,
  isLoadingPlans: false,
  error: null,
  historyError: null,
  plansError: null,
};

export const fetchUserSubscription = createAsyncThunk(
  "subscription/fetchUserSubscription",
  async (_, { rejectWithValue }) => {
    try {
      const response = await SubscriptionService.getUserSubscription();
      const subscription = response.data;

      if (subscription) {
        saveMySubscription(subscription);
      } else {
        clearMySubscription();
      }

      return subscription;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to load subscription");
    }
  },
);

export const fetchSubscriptionHistory = createAsyncThunk(
  "subscription/fetchSubscriptionHistory",
  async (params: GetSubscriptionParams = {}, { rejectWithValue }) => {
    try {
      const response = await SubscriptionService.getSubscriptionHistory(params);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to load subscription history");
    }
  },
);

export const fetchSubscriptionPlans = createAsyncThunk(
  "subscription/fetchSubscriptionPlans",
  async (_, { rejectWithValue }) => {
    try {
      const response = await RazorpayService.getSubscriptionPlans();
      return response.data?.results ?? [];
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to load membership plans");
    }
  },
  {
    condition: (_, { getState }) => {
      const { subscription } = getState() as { subscription: SubscriptionState };

      if (subscription.plans.length > 0 || subscription.isLoadingPlans) {
        return false;
      }

      return true;
    },
  },
);

const subscriptionSlice = createSlice({
  name: "subscription",
  initialState,
  reducers: {
    clearSubscription: (state) => {
      clearMySubscription();
      state.currentSubscription = null;
      state.subscriptionHistory = [];
      state.plans = [];
      state.error = null;
      state.historyError = null;
      state.plansError = null;
      state.isLoadingCurrent = false;
      state.isLoadingHistory = false;
      state.isLoadingPlans = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserSubscription.pending, (state) => {
        state.isLoadingCurrent = true;
        state.error = null;
      })
      .addCase(fetchUserSubscription.fulfilled, (state, action) => {
        state.isLoadingCurrent = false;
        state.currentSubscription = action.payload;
        state.error = null;
      })
      .addCase(fetchUserSubscription.rejected, (state, action) => {
        state.isLoadingCurrent = false;
        state.error = action.payload as string;
      })
      .addCase(fetchSubscriptionHistory.pending, (state) => {
        state.isLoadingHistory = true;
        state.historyError = null;
      })
      .addCase(fetchSubscriptionHistory.fulfilled, (state, action) => {
        state.isLoadingHistory = false;
        const historyData = action.payload as GetSubscriptionResponse<SubscribedUser>;
        state.subscriptionHistory = historyData.results || [];
        state.historyError = null;
      })
      .addCase(fetchSubscriptionHistory.rejected, (state, action) => {
        state.isLoadingHistory = false;
        state.historyError = action.payload as string;
      })
      .addCase(fetchSubscriptionPlans.pending, (state) => {
        state.isLoadingPlans = true;
        state.plansError = null;
      })
      .addCase(fetchSubscriptionPlans.fulfilled, (state, action) => {
        state.isLoadingPlans = false;
        state.plans = action.payload;
        state.plansError = null;
      })
      .addCase(fetchSubscriptionPlans.rejected, (state, action) => {
        state.isLoadingPlans = false;
        state.plansError = action.payload as string;
      });
  },
});

export const { clearSubscription } = subscriptionSlice.actions;
export default subscriptionSlice.reducer;
